import useTypographyStyles from "hooks/useTypographyStyles";
import RHArrowIcon from "icon-arrow";
import RHCloseIcon from "icon-close";
import React, { FC } from "react";
import { createStyles, makeStyles } from "@mui/styles";
import { DialogTitle, DialogTitleProps, Theme } from "@mui/material";
import { TailwindTypography as Typography } from "@RHCommerceDev/component-tailwind-typography";
import Button from "@RHCommerceDev/component-tailwind-button";
import { cn } from "@RHCommerceDev/utils/cn";

export interface RHDialogTitleProps extends DialogTitleProps {
  onBack?: (event: {}, reason: "backdropClick" | "escapeKeyDown") => void;
  onClose?: (event: {}, reason: "backdropClick" | "escapeKeyDown") => void;
  titleClassNames?: string;
  titlePaddingClassNames?: string;
  closeIconClassnames?: string;
  closeButtonRootClassnames?: string;
  subTitle?: string;
  anchor?: string;
  closeIconClass?: string;
  isDark?: boolean; //keep this key as it is needed for concierge.
}

export const RHDialogTitle: FC<RHDialogTitleProps> = ({
  onBack,
  onClose,
  title,
  titleClassNames,
  titlePaddingClassNames,
  closeButtonRootClassnames,
  children,
  subTitle,
  anchor,
  closeIconClass,
  ...rest
}) => {
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      icon: {
        top: "50%",
        transform: "translateY(-50%)"
      },
      title_padding: {
        padding: theme.spacing(3, 4),
        [theme.breakpoints.up("xs")]: {
          padding: theme.spacing(3.4, 4, 3, 5),
          ...rest.style
        },
        [theme.breakpoints.up("sm")]: {
          padding: theme.spacing(4, 4, 3, 5),
          ...rest.style
        },
        [theme.breakpoints.up("xl")]: {
          padding: theme.spacing(7.62, 4, 3, 10),
          ...rest.style
        }
      }
    })
  );

  const classes = useTypographyStyles({
    keys: ["textCenter", "rhBaseCaption", "rhBaseBody1"]
  });

  const iconClasses = useStyles();
  return (
    <DialogTitle
      component="div"
      id={"component-rh-dialog-title"}
      disableTypography
      {...rest}
      style={{ ...rest.style }}
      className={cn(
        `relative right-0 top-0 z-[1] flex bg-[#f9f7f4]`,
        //We can put this line back once we have a confirmation modal popup that has a main title
        //Make sure to change position: "absolute" to position: "relative" too
        iconClasses.title_padding,
        titlePaddingClassNames
      )}
    >
      {onBack && (
        <Button
          disableRipple
          className={cn(iconClasses.icon, "!absolute left-1.5 z-20 p-[10px]")}
          color="inherit"
          onClick={event => onBack(event, "escapeKeyDown")}
        >
          <RHArrowIcon
            style={{
              transform: "rotate(180deg)"
            }}
          />
        </Button>
      )}

      {title && (
        <div className="flex flex-col">
          <Typography
            className={cn(
              [
                { [classes.textCenter]: !Boolean(titleClassNames) },
                titleClassNames ? titleClassNames : classes.rhBaseBody1
              ],
              "break-words !text-left uppercase"
            )}
            dangerouslySetInnerHTML={{
              __html: title
            }}
          />
          {subTitle && (
            <Typography
              className={cn(
                [
                  { [classes.textCenter]: !Boolean(titleClassNames) },
                  titleClassNames,
                  classes.rhBaseCaption
                ],
                "break-words !text-left"
              )}
              dangerouslySetInnerHTML={{
                __html: subTitle
              }}
            />
          )}
        </div>
      )}
      {children}
      {onClose && (
        <Button
          disableRipple
          className={cn(
            iconClasses.icon,
            `!absolute
            !right-0 w-[48px] !justify-start
             !p-4`,
            "focus-visible:outline-auto !text-[20px] focus-visible:outline focus-visible:outline-[1px] focus-visible:outline-[#015fcc]",
            anchor === "right" ? "-left-[46px]" : "",
            anchor === "left" ? "-right-[58px]" : "",
            rest.closeIconClassnames
          )}
          aria-label="Close"
          color="inherit"
          tabIndex={0}
          onClick={event => onClose(event, "escapeKeyDown")}
          data-testid="dialog-title-close-button"
        >
          <RHCloseIcon className={cn(["!text-[2rem]", closeIconClass])} />
        </Button>
      )}
    </DialogTitle>
  );
};

RHDialogTitle.defaultProps = {};

export default RHDialogTitle;
